import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Warehouse, Plus, Upload, Pencil, Trash2, Search, Loader2 } from "lucide-react";

const FIELDS = [["name", "Nom *"], ["city", "Ville"], ["address", "Adresse"], ["phone", "Téléphone"], ["fax", "Fax"], ["email", "Email"]];

function GrossisteForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/admin/grossistes/${initial.id}`, form);
      else await api.post("/admin/grossistes", form);
      toast.success(initial?.id ? "Grossiste mis à jour" : "Grossiste ajouté");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="grossiste-form-modal">
        <DialogHeader><DialogTitle>{initial?.id ? "Modifier le grossiste" : "Nouveau grossiste"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={k === "address" || k === "name" ? "col-span-2" : ""}>
              <label className="text-xs font-semibold text-slate-600">{l}</label>
              <input required={k === "name"} type={k === "email" ? "email" : "text"} value={form[k] || ""} data-testid={`grossiste-input-${k}`}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
          ))}
          <button disabled={saving} data-testid="grossiste-save" className="col-span-2 mt-2 h-10 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200 disabled:opacity-60">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminGrossistes() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/grossistes").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const importFile = async (f) => {
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/grossistes/import", fd);
      toast.success(`Import terminé : ${data.created} ajouté(s), ${data.updated} mis à jour`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Supprimer ${g.name} ?`)) return;
    await api.delete(`/admin/grossistes/${g.id}`);
    toast.success("Grossiste supprimé");
    load();
  };

  if (items === null) return <Loading />;
  const t = q.trim().toLowerCase();
  const list = t ? items.filter((g) => [g.name, g.city, g.address].some((v) => (v || "").toLowerCase().includes(t))) : items;

  return (
    <div>
      <PageHeader title="Grossistes répartiteurs" subtitle="Liste des grossistes proposés aux pharmaciens pour la livraison.">
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="grossiste-import-input"
            onChange={(e) => { importFile(e.target.files[0]); e.target.value = ""; }} />
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="grossiste-import-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-colors duration-200 disabled:opacity-60">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importer Excel
          </button>
          <button onClick={() => setEditing({})} data-testid="grossiste-add-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200">
            <Plus className="h-4 w-4" /> Ajouter
          </button>
        </div>
      </PageHeader>
      <p className="text-xs text-slate-500 mb-4">Colonnes reconnues : Nom, Adresse, Téléphone, Fax, Ville, Email. Un grossiste déjà présent (même nom) est mis à jour, les nouveaux sont ajoutés.</p>

      <div className="relative max-w-sm mb-4">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher nom, ville…" data-testid="grossiste-search"
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600" />
      </div>
      <div className="text-sm font-semibold text-slate-500 mb-3" data-testid="grossiste-count">{list.length} grossiste(s)</div>

      {list.length === 0 ? <EmptyState title="Aucun grossiste" icon={Warehouse} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              {["Nom", "Ville", "Adresse", "Téléphone", "Email", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((g) => (
                <tr key={g.id} className="hover:bg-slate-50 transition-colors duration-200" data-testid={`grossiste-row-${g.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{g.name}</td>
                  <td className="px-4 py-3 text-slate-600">{g.city || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-xs truncate">{g.address || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 num whitespace-nowrap">{g.phone || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{g.email || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => setEditing(g)} data-testid={`grossiste-edit-${g.id}`} className="p-1.5 rounded-md hover:bg-indigo-50 text-indigo-600 transition-colors duration-200"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(g)} data-testid={`grossiste-delete-${g.id}`} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600 transition-colors duration-200"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <GrossisteForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
